import React, { useEffect, useState, useCallback } from 'react';
import { getSmsPreferences, updateSmsPreferences } from '../api';

/** Show stored E.164 numbers without the +1 country code in the form. */
function formatPhoneForDisplay(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length === 10) return digits;
  return value;
}

/** Pretty format for the subscribed summary, e.g. (503) 575-0700 */
function formatPhonePretty(value) {
  const digits = formatPhoneForDisplay(value);
  if (digits.length !== 10) return digits || value;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function frequencyLabel(freq) {
  if (freq === 'weekly') return 'Weekly';
  if (freq === 'monthly') return 'Monthly';
  return 'Off';
}

/**
 * Client-facing SMS budget notification preferences.
 */
function SmsPreferences({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(true);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [frequency, setFrequency] = useState('monthly');
  const [optedInAt, setOptedInAt] = useState(null);
  const [optedOutAt, setOptedOutAt] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getSmsPreferences(clientId);
      const p = data.preferences || {};
      const displayPhone = formatPhoneForDisplay(p.phoneNumber || '');
      const isSubscribed = !!p.smsOptIn && !!displayPhone;
      setPhoneNumber(displayPhone);
      setSmsOptIn(!!p.smsOptIn);
      setFrequency(p.smsNotificationFrequency || 'monthly');
      setOptedInAt(p.smsOptedInAt || null);
      setOptedOutAt(p.smsOptedOutAt || null);
      setEditing(!isSubscribed);
    } catch (err) {
      const data = err?.response?.data;
      const apiMsg = typeof data === 'string' ? null : data?.error;
      setError(apiMsg || 'Could not load your SMS notification settings.');
      setEditing(true);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const persist = async (patch, successMsg, { returnToSummary = false } = {}) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = await updateSmsPreferences(clientId, patch);
      const p = data.preferences || {};
      const displayPhone = formatPhoneForDisplay(p.phoneNumber || '');
      const isSubscribed = !!p.smsOptIn && !!displayPhone;
      setPhoneNumber(displayPhone);
      setSmsOptIn(!!p.smsOptIn);
      setFrequency(p.smsNotificationFrequency || 'monthly');
      setOptedInAt(p.smsOptedInAt || null);
      setOptedOutAt(p.smsOptedOutAt || null);
      setSuccess(successMsg || 'Your SMS preferences were saved.');
      if (returnToSummary && isSubscribed) {
        setEditing(false);
      }
      if (!p.smsOptIn) {
        setEditing(true);
      }
    } catch (err) {
      console.error('SMS save failed:', err?.response?.status, err?.response?.data, err?.message);
      const data = err?.response?.data;
      const apiMsg =
        typeof data === 'string'
          ? null
          : (data?.error || data?.message || null);
      const msg =
        apiMsg ||
        (err?.response?.status === 404
          ? 'SMS settings endpoint not found. The app may need to be redeployed.'
          : err?.response?.status === 503
            ? 'SMS is not set up in the database yet. Your advisor needs to run migration 024 in Supabase.'
            : err?.response?.status === 401
              ? 'Your session expired. Please sign out and sign in again.'
              : err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK'
                ? 'Could not reach the server. The backend may be waking up — wait a moment and try again.'
                : err?.response?.status
                  ? `Could not save SMS preferences (server error ${err.response.status}).`
                  : 'Could not save your SMS preferences.');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    persist(
      { phoneNumber, smsOptIn, smsNotificationFrequency: frequency },
      smsOptIn ? 'You are opted in to SMS budget notifications.' : 'Preferences saved.',
      { returnToSummary: smsOptIn }
    );
  };

  const handleOptOut = () => {
    persist(
      { smsOptIn: false, smsNotificationFrequency: 'off' },
      'You have been opted out of SMS notifications.'
    );
  };

  const showSummary = smsOptIn && phoneNumber && !editing;

  if (loading) {
    return (
      <div className="sms-preferences-card">
        <h3>Text Message Notifications</h3>
        <p className="sms-preferences-intro">Loading your settings…</p>
      </div>
    );
  }

  return (
    <div className="sms-preferences-card">
      <h3>Text Message Notifications</h3>
      <p className="sms-preferences-intro">
        Get a short monthly text with a high-level budget check. Messages never include account
        numbers, balances, or transaction details.
      </p>

      {error && <div className="sms-preferences-alert error">{error}</div>}
      {success && <div className="sms-preferences-alert success">{success}</div>}

      {showSummary ? (
        <div className="sms-summary">
          <div className="sms-summary-badge">Subscribed</div>
          <p className="sms-summary-phone">{formatPhonePretty(phoneNumber)}</p>
          <p className="sms-summary-meta">
            {frequencyLabel(frequency)}
            {optedInAt && (
              <> · Opted in {new Date(optedInAt).toLocaleDateString()}</>
            )}
          </p>
          <button
            type="button"
            className="sms-btn-opt-out"
            onClick={handleOptOut}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Opt out'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <div className="sms-phone-field">
            <label htmlFor="sms-phone">Mobile number</label>
            <input
              id="sms-phone"
              type="tel"
              className="sms-phone-input"
              placeholder="5035551234"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d()-.\s]/g, ''))}
              autoComplete="tel"
              inputMode="numeric"
              maxLength={14}
            />
            <small>US mobile number (10 digits, no country code). Dashes or parentheses are fine.</small>
          </div>

          <div className="sms-field">
            <label htmlFor="sms-frequency">Frequency</label>
            <select
              id="sms-frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <option value="off">Off</option>
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          <div className="sms-consent-row">
            <input
              id="sms-opt-in"
              type="checkbox"
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              style={{ marginTop: '4px' }}
            />
            <label htmlFor="sms-opt-in">
              I consent to receive automated SMS budget notifications at the number above from my
              financial advisor. I understand I can reply <strong>STOP</strong> at any time to opt out,
              or <strong>HELP</strong> for assistance. Message frequency varies; message and data rates
              may apply. Consent is not a condition of any service. See our{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
              <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a>.
            </label>
          </div>

          <div className="sms-form-actions">
            <button type="submit" className="sms-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save preferences'}
            </button>
          </div>
        </form>
      )}

      {showSummary && (
        <button
          type="button"
          className="sms-edit-link"
          onClick={() => {
            setSuccess('');
            setError('');
            setEditing(true);
          }}
        >
          Update phone or frequency
        </button>
      )}

      <div className="sms-preferences-footer">
        {optedOutAt && !smsOptIn && (
          <div>Opted out on {new Date(optedOutAt).toLocaleDateString()}.</div>
        )}
        <div>Not financial advice. Messages are informational summaries only.</div>
      </div>
    </div>
  );
}

export default SmsPreferences;
